import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Award,
  Check,
  ChevronLeft,
  Circle,
  ClipboardList,
  Clock,
  Download,
  Lock,
  Play,
  XCircle,
} from 'lucide-react';
import { formatClock } from '@mlm/shared';
import {
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Progress,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  UpNextCard,
  toast,
  type PlaybackAdapter,
  type PlayerControls,
} from '@mlm/ui';
import { CourseReviewForm } from '../storefront/components/course-review-form';
import { ReportDialog } from '../components/report-dialog';
import { LessonPlayer } from '../components/lesson-player';
import { LessonQa } from '../components/lesson-qa';
import { CourseAnnouncements } from '../components/course-announcements';
import { LessonNotes } from '../components/lesson-notes';
import { CertificateCanvas } from '../components/certificate-canvas';
import { CourseTestPane } from '../components/course-test-pane';
import { useLessonHeartbeat } from '../components/use-lesson-heartbeat';
import { usePlayerKeyboard } from '../components/use-player-keyboard';
import {
  completeLesson,
  createLessonNote,
  downloadLessonFile,
  getCoursePlayer,
  getMyCertificate,
  getMyCourseReview,
  getPublicCourse,
  getTenantConfig,
  openLesson,
  type LessonOutline,
  type LessonSource,
  uncompleteLesson,
} from '../lib/api';
import { buildCourseSequence, resolveNext, resolvePrev, type NextVerdict } from '@mlm/shared';
import { decideUpNext, UP_NEXT_COUNTDOWN_SECONDS } from '@mlm/shared';
import { usePlayerPrefs } from '../lib/player/prefs-store';

/** "Sep 12, 2026" — how every drip lock is spelled to the learner. */
function unlockDate(iso: string | null | undefined): string {
  if (!iso) return 'a later date';
  const at = new Date(iso);
  return Number.isNaN(at.getTime())
    ? 'a later date'
    : at.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Course player (`/learn/:courseId?lesson=`) — the "My Courses → Resume"
 * destination: chapter/lesson sidebar with completion checkmarks, the player
 * pane (embed or R2 video, resolved per lesson by the gated `open` endpoint),
 * lesson notes, and the manual Mark-complete toggle (player `ended` events
 * auto-complete but are best-effort). A denied access renders a locked state
 * with the right CTA — never a 403 page.
 */
export function CoursePlayerPage() {
  const { courseId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const playerQ = useQuery({
    queryKey: ['course-player', courseId],
    queryFn: () => getCoursePlayer(courseId),
    enabled: !!courseId,
    retry: 0,
  });
  const player = playerQ.data;
  const publicQ = useQuery({
    queryKey: ['public-course', player?.productId],
    queryFn: () => getPublicCourse(player!.productId),
    enabled: !!player?.productId,
    retry: 0,
  });
  const lessons = useMemo(
    () => (player?.chapters ?? []).flatMap((c) => c.lessons.map((l) => ({ ...l, chapter: c }))),
    [player],
  );
  const lessonTitles = useMemo(() => new Map(lessons.map((l) => [l.id, l.title])), [lessons]);
  // Tenant policy: no skipping past what has been watched. Shares the one
  // cached tenant-config fetch, and arrives already ANDed with `lms_enabled`.
  const seekRestricted =
    useQuery({ queryKey: ['tenant-config'], queryFn: getTenantConfig }).data?.lmsSeekRestriction ===
    true;

  const progressByLesson = useMemo(
    () => new Map((player?.progress ?? []).map((p) => [p.lessonId, p])),
    [player],
  );
  const wanted = params.get('lesson');
  // A selected test swaps the video pane for the test pane.
  const wantedTest = params.get('test');
  const currentTest = (player?.tests ?? []).find((t) => t.id === wantedTest) ?? null;
  // Default lesson: the last opened, else the first.
  const currentId = useMemo(() => {
    if (wanted && lessons.some((l) => l.id === wanted)) return wanted;
    const recent = [...(player?.progress ?? [])].sort(
      (a, b) => new Date(b.lastOpenedAt).getTime() - new Date(a.lastOpenedAt).getTime(),
    )[0];
    return recent?.lessonId ?? lessons[0]?.id ?? null;
  }, [wanted, lessons, player]);
  const current = currentTest ? null : (lessons.find((l) => l.id === currentId) ?? null);
  const [source, setSource] = useState<LessonSource | null>(null);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [adapter, setAdapter] = useState<PlaybackAdapter | null>(null);
  const controlsRef = useRef<PlayerControls | null>(null);
  const [upNext, setUpNext] = useState<NextVerdict | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const pendingSeekRef = useRef<number | null>(null);
  const autoplayNext = usePlayerPrefs((s) => s.autoplay);
  const setAutoplayNext = usePlayerPrefs((s) => s.setAutoplay);

  // `playable` is source readiness and `access.allowed` is entitlement. The
  // per-lesson drip verdict is deliberately NOT part of this: `/open` is the
  // authority on a lock, and it is also the only call that creates the
  // enrollment a free course's drip clock counts from — gating it here would
  // strand a deep-linked learner on a course that never unlocks.
  const canPlay = (l: LessonOutline) => (player?.access.allowed || l.isPreview) && l.playable;

  const currentLessonId = current?.id ?? null;
  const currentPlayable = current ? canPlay(current) : false;

  // Resolve the source whenever the current lesson changes.
  useEffect(() => {
    setSource(null);
    setSourceError(null);
    setUpNext(null);
    setCountdown(null);
    if (!currentLessonId || !currentPlayable) return;
    let cancelled = false;
    openLesson(courseId, currentLessonId)
      .then((s) => {
        if (cancelled) return;
        setSource(s);
        qc.invalidateQueries({ queryKey: ['course-player', courseId] });
        qc.invalidateQueries({ queryKey: ['my-courses'] });
        qc.invalidateQueries({ queryKey: ['learning-dashboard'] });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const data = (e as { response?: { data?: { error?: string; availableAt?: string } } })
          ?.response?.data;
        setSourceError(
          data?.error === 'lesson_video_not_ready'
            ? 'This video is still being processed.'
            : data?.error === 'subscription_required'
              ? 'Your subscription has lapsed.'
              : data?.error === 'lesson_not_available'
                ? `This lesson unlocks on ${unlockDate(data.availableAt)}.`
                : data?.error === 'lesson_source_blocked'
                  ? // The tenant removed this video's host from its allowed
                    // list. Nothing the learner can do, so say who can fix it.
                    'This video is hosted somewhere this site no longer allows. The instructor needs to re-upload it.'
                  : 'This lesson could not be loaded.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [courseId, currentLessonId, currentPlayable, qc]);

  // Re-presign the CURRENT lesson in place. Deliberately not the effect above:
  // this must only swap the src — invalidating the player/my-courses caches
  // mid-lesson would re-render the whole page around a playing video. The
  // `refresh` flag also keeps the swap out of the view/open counters.
  const refreshSource = useCallback(async (): Promise<LessonSource | null> => {
    const lessonId = current?.id;
    if (!lessonId) return null;
    try {
      const next = await openLesson(courseId, lessonId, { refresh: true });
      setSource((prev) => (prev?.lessonId === lessonId ? next : prev));
      return next;
    } catch {
      return null;
    }
  }, [courseId, current?.id]);

  const complete = useMutation({
    meta: { errorMessage: "Couldn't save your progress — please try again." },
    mutationFn: ({ lessonId, via }: { lessonId: string; via: 'player' | 'manual' }) =>
      completeLesson(courseId, lessonId, via),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['course-player', courseId] });
      qc.invalidateQueries({ queryKey: ['my-courses'] });
      if (r.courseComplete && r.newlyCompleted) {
        toast.success(
          r.testsPassed
            ? 'Course complete — well done!'
            : 'Lessons complete — pass the test to earn your certificate.',
        );
      }
    },
  });
  const uncomplete = useMutation({
    meta: { errorMessage: "Couldn't update that lesson — please try again." },
    mutationFn: (lessonId: string) => uncompleteLesson(courseId, lessonId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['course-player', courseId] });
      qc.invalidateQueries({ queryKey: ['my-courses'] });
    },
  });
  const bookmark = useMutation({
    meta: { errorMessage: "Couldn't save that bookmark — please try again." },
    mutationFn: ({
      lessonId,
      positionSeconds,
    }: {
      lessonId: string;
      positionSeconds: number | null;
    }) => createLessonNote(courseId, lessonId, { positionSeconds, body: '' }),
    onSuccess: (_note, vars) => {
      void qc.invalidateQueries({ queryKey: ['lesson-notes', courseId] });
      const label = vars.positionSeconds != null ? ` ${formatClock(vars.positionSeconds)}` : '';
      // Inside fullscreen the app-root toaster renders nowhere, so the player's
      // own in-frame notice is the one the learner actually sees.
      controlsRef.current?.flash(`Bookmarked${label}`);
      toast.success(`Bookmarked${label}`);
    },
  });

  const goTo = useCallback(
    (lessonId: string, replace = false) => setParams({ lesson: lessonId }, { replace }),
    [setParams],
  );
  const goToTest = useCallback(
    (testId: string) => setParams({ test: testId }, { replace: false }),
    [setParams],
  );
  const done = lessons.filter((l) => progressByLesson.get(l.id)?.completedAt).length;
  const pct = lessons.length ? Math.round((done / lessons.length) * 100) : 0;
  const allDone = lessons.length > 0 && done === lessons.length;
  // Driven by `?cert=1` so a `certificate_issued` bell opens the certificate
  // even when the learner is ALREADY on this player — `/learn/:courseId` is
  // one route, so a bell click there changes only the search string and a
  // useState initialiser would never re-run.
  const certParam = params.get('cert') === '1';
  const [showCert, setShowCert] = useState(certParam);
  useEffect(() => {
    if (certParam) setShowCert(true);
  }, [certParam]);
  const certQ = useQuery({
    queryKey: ['my-certificate', courseId],
    queryFn: () => getMyCertificate(courseId),
    // A template-less course can only 404 here, so don't ask: the completion
    // card says so in words instead. One retry, because a transient failure on
    // the last step of a course should not read as "no certificate".
    enabled:
      !!player?.certificateEligible && !!player?.hasCertificateTemplate && !!player?.access.allowed,
    retry: 1,
  });
  // Prompt for a review at the finish line, where the learner has just formed
  // an opinion — only asked once they can actually post one.
  const [reviewOpen, setReviewOpen] = useState(false);
  const myReviewQ = useQuery({
    queryKey: ['course-review-mine', courseId],
    queryFn: () => getMyCourseReview(courseId),
    // Asked for ANY learner with access, not only one finishing a course that
    // issues a certificate. The old gate borrowed the certificate query's
    // conditions, so on a course with no template — or before the finish line —
    // the query never ran, `eligible` stayed undefined, and the product had no
    // rating affordance anywhere a learner could find it.
    enabled: !!player?.access.allowed,
    retry: 1,
  });
  const myReview = myReviewQ.data ?? null;

  // Only a GRADED test can stand between the learner and their certificate, so
  // only a graded test may be named in the "pass this next" banner.
  const firstUnpassedTest = (player?.tests ?? []).find((t) => !t.isPractice && !t.passed) ?? null;
  const currentDone = !!(current && progressByLesson.get(current.id)?.completedAt);

  // One ordered walk of lessons AND quizzes, so Next stops skipping chapter
  // tests. Quizzes gate the certificate, never progression — a learner whose
  // attempts are exhausted is carried past rather than trapped.
  const sequence = useMemo(
    () =>
      buildCourseSequence(
        player?.chapters ?? [],
        (player?.tests ?? []).map((t) => ({ ...t, attemptCount: t.attempts.length })),
      ),
    [player],
  );
  const nextVerdict = useMemo(
    () => resolveNext({ sequence, currentId: currentTest?.id ?? currentLessonId }),
    [sequence, currentLessonId, currentTest],
  );
  const prevLesson = useMemo(
    () => resolvePrev({ sequence, currentId: currentTest?.id ?? currentLessonId }),
    [sequence, currentLessonId, currentTest],
  );

  // Resume point, frozen for the life of the lesson. Recomputing it from the
  // live payload would let the player's own heartbeat writes feed back in and
  // re-seek a video that is already playing.
  const progressRef = useRef(progressByLesson);
  progressRef.current = progressByLesson;
  const [resumeAt, setResumeAt] = useState<number | null>(null);
  useEffect(() => {
    setResumeAt(
      currentLessonId ? (progressRef.current.get(currentLessonId)?.positionSeconds ?? null) : null,
    );
  }, [currentLessonId]);

  const heartbeat = useLessonHeartbeat({
    courseId,
    lessonId: currentLessonId,
    // This page sits behind RequireAuth and `/open` has already created the
    // progress row — even for a preview lesson on an unpurchased course, so
    // resume works there too. (The write is a user-scoped UPDATE, so a missing
    // row is a harmless no-op.) The logged-out storefront preview wires no
    // heartbeat at all.
    guest: false,
  });

  usePlayerKeyboard({
    // Never while a test pane is open: a learner mid-essay must not lose a key.
    enabled: !!adapter && !currentTest,
    adapter,
    controls: controlsRef.current,
    kind: source?.kind === 'video' ? 'video' : 'iframe',
    onBookmark: () =>
      currentLessonId &&
      bookmark.mutate({
        lessonId: currentLessonId,
        positionSeconds: adapter ? Math.floor(adapter.getSnapshot().currentTime) : null,
      }),
  });

  // Auto-advance ticks here, not in the card: navigation is the page's job.
  const upNextRef = useRef<NextVerdict | null>(null);
  upNextRef.current = upNext;
  useEffect(() => {
    if (countdown == null) return;
    if (countdown <= 0) {
      setCountdown(null);
      const verdict = upNextRef.current;
      // `replace` on an auto-advance: Back should leave the course, not walk
      // the learner backwards through everything that played itself.
      if (verdict?.kind === 'lesson') setParams({ lesson: verdict.lesson.id }, { replace: true });
      else if (verdict?.kind === 'quiz') setParams({ test: verdict.test.id }, { replace: false });
      return;
    }
    const timer = setTimeout(() => setCountdown((n) => (n == null ? null : n - 1)), 1000);
    return () => clearTimeout(timer);
  }, [countdown, setParams]);

  const onPlayerEnded = useCallback(() => {
    if (currentLessonId && !currentDone) {
      complete.mutate({ lessonId: currentLessonId, via: 'player' });
    }
    const plan = decideUpNext({
      next: nextVerdict,
      autoplayOn: autoplayNext,
      sourceKind: source?.kind === 'article' ? 'article' : (source?.kind ?? 'video'),
    });
    if (!plan) return;
    setUpNext(nextVerdict);
    setCountdown(plan.variant === 'countdown' ? UP_NEXT_COUNTDOWN_SECONDS : null);
    // A drip boundary that has just passed should re-resolve, not stay shut.
    if (plan.variant === 'locked') {
      void qc.invalidateQueries({ queryKey: ['course-player', courseId] });
    }
  }, [currentLessonId, currentDone, nextVerdict, autoplayNext, source, courseId, complete, qc]);

  const seekTo = useCallback(
    (seconds: number, lessonId: string) => {
      if (lessonId === currentLessonId) {
        adapter?.seek(seconds);
        return;
      }
      // Another lesson: navigate first, then seek once its player is ready.
      pendingSeekRef.current = seconds;
      goTo(lessonId);
    },
    [adapter, currentLessonId, goTo],
  );

  // A note chip from another lesson: apply the parked seek once playback is up.
  useEffect(() => {
    if (!adapter || pendingSeekRef.current == null) return;
    const target = pendingSeekRef.current;
    pendingSeekRef.current = null;
    const timer = setTimeout(() => adapter.seek(target), 400);
    return () => clearTimeout(timer);
  }, [adapter, currentLessonId]);

  const handleAdapter = useCallback(
    (next: PlaybackAdapter | null, controls: PlayerControls | null) => {
      controlsRef.current = controls;
      setAdapter(next);
    },
    [],
  );

  // An article needs a DEFINITE height, not a 16:9 letterbox: its reading pane
  // scrolls with `h-full`, which only resolves against a parent whose height is
  // set.
  const paneClass = source?.kind === 'article' ? 'h-[60vh] border' : 'aspect-video bg-black';

  const upNextOverlay = (() => {
    if (!upNext) return null;
    const nextTitle =
      upNext.kind === 'lesson' || upNext.kind === 'locked'
        ? upNext.lesson.title
        : upNext.kind === 'quiz'
          ? upNext.test.title
          : 'You’ve finished the last lesson.';
    const dismiss = () => {
      setUpNext(null);
      setCountdown(null);
    };
    if (upNext.kind === 'end') return <UpNextCard variant="end" title={nextTitle} />;
    if (upNext.kind === 'locked') {
      return (
        <UpNextCard
          variant="locked"
          title={nextTitle}
          detail={`Unlocks on ${unlockDate(upNext.availableAt)}.`}
          primaryLabel="Back to course content"
          onPrimary={dismiss}
        />
      );
    }
    if (upNext.kind === 'quiz') {
      return (
        <UpNextCard
          variant="quiz"
          title={upNext.test.title}
          detail={
            upNext.test.isPractice
              ? 'Optional — check what you have learned. It never affects your certificate.'
              : 'Pass it to keep your certificate on track.'
          }
          primaryLabel={upNext.test.isPractice ? 'Start practice' : 'Start quiz'}
          onPrimary={() => goToTest(upNext.test.id)}
        />
      );
    }
    return (
      <UpNextCard
        variant={countdown != null ? 'countdown' : 'play'}
        title={nextTitle}
        detail={
          upNext.lesson.durationSeconds ? formatClock(upNext.lesson.durationSeconds) : undefined
        }
        secondsLeft={countdown ?? undefined}
        autoplay={autoplayNext}
        onToggleAutoplay={setAutoplayNext}
        primaryLabel={countdown != null ? 'Play now' : 'Play next lesson'}
        onPrimary={() => goTo(upNext.lesson.id, true)}
        onCancel={countdown != null ? () => setCountdown(null) : undefined}
      />
    );
  })();

  if (playerQ.isError) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        This course isn't available.{' '}
        <Link to="/my-courses" className="underline">
          Back to My Courses
        </Link>
      </div>
    );
  }
  if (!player) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

  return (
    <div className="mx-auto max-w-6xl space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <button
            onClick={() => navigate('/my-courses')}
            className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
          >
            <ChevronLeft className="h-4 w-4" /> My Courses
          </button>
          {publicQ.data && (
            <>
              <div className="truncate text-sm font-semibold">{publicQ.data.name}</div>
              {publicQ.data.subtitle && (
                <div className="truncate text-xs text-muted-foreground">
                  {publicQ.data.subtitle}
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-3 text-sm">
          <span className="text-muted-foreground">
            {done}/{lessons.length} complete
          </span>
          <div className="w-32">
            <Progress value={pct} />
          </div>
          {/* Rating and reporting live HERE, not only on the completion card at
              the bottom of a finished course: a learner who wants to rate (or
              report) mid-course had nowhere to do either, and the storefront
              form is three navigations away. */}
          {myReview?.eligible ? (
            <Button size="sm" variant="ghost" onClick={() => setReviewOpen(true)}>
              {myReview.review ? 'Edit your rating' : 'Rate this course'}
            </Button>
          ) : myReview?.reason === 'no_progress' ? (
            <span className="text-xs text-muted-foreground">
              Finish a lesson to rate this course
            </span>
          ) : null}
          <ReportDialog targetKind="course" targetId={courseId} label="Report course" />
        </div>
      </div>

      {!player.access.allowed && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4 text-sm">
            <span className="flex items-center gap-2">
              <Lock className="h-4 w-4" />
              {player.access.reason === 'subscription_required'
                ? 'This course is for subscribers — your subscription is not active.'
                : 'You have not purchased this course. Preview lessons are open.'}
            </span>
            {player.access.reason === 'subscription_required' ? (
              <Button asChild size="sm">
                <Link to="/subscribe">Subscribe</Link>
              </Button>
            ) : (
              <Button asChild size="sm">
                <Link to={`/course/${player.productId}`}>View course</Link>
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {allDone && !player.testsPassed && player.access.allowed && firstUnpassedTest && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4 text-sm">
            <span className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              Lessons done! Pass “{firstUnpassedTest.title}” to earn your certificate.
            </span>
            <Button size="sm" onClick={() => goToTest(firstUnpassedTest.id)}>
              Take the test
            </Button>
          </CardContent>
        </Card>
      )}

      {player.certificateEligible && player.access.allowed && (
        <Card>
          <CardContent className="flex flex-wrap items-center justify-between gap-3 py-4 text-sm">
            <span>
              🎉 You've completed this course.
              {!player.hasCertificateTemplate
                ? " This course doesn't issue a certificate."
                : certQ.data
                  ? ' Your certificate is ready.'
                  : certQ.isError
                    ? " We couldn't load your certificate."
                    : ''}
            </span>
            <span className="flex flex-wrap items-center gap-3">
              {myReviewQ.data?.eligible &&
                (myReviewQ.data.review ? (
                  <span className="text-muted-foreground">Thanks for rating this course.</span>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setReviewOpen(true)}>
                    How was this course?
                  </Button>
                ))}
              {certQ.data && (
                <Button size="sm" variant="outline" onClick={() => setShowCert((v) => !v)}>
                  {showCert ? 'Hide certificate' : 'View certificate'}
                </Button>
              )}
              {player.hasCertificateTemplate && certQ.isError && (
                <Button size="sm" variant="outline" onClick={() => void certQ.refetch()}>
                  Try again
                </Button>
              )}
            </span>
          </CardContent>
          {showCert && certQ.data && (
            <CardContent className="pt-0">
              <CertificateCanvas cert={certQ.data} />
            </CardContent>
          )}
        </Card>
      )}

      <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>How was this course?</DialogTitle>
          </DialogHeader>
          <CourseReviewForm
            courseId={courseId}
            existing={myReviewQ.data?.review ?? null}
            onSaved={() => {
              setReviewOpen(false);
              qc.invalidateQueries({ queryKey: ['course-review-mine', courseId] });
              qc.invalidateQueries({ queryKey: ['reviews', player.productId] });
              qc.invalidateQueries({ queryKey: ['public-course', player.productId] });
            }}
          />
        </DialogContent>
      </Dialog>

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div className="min-w-0 space-y-3">
          {currentTest ? (
            <CourseTestPane
              courseId={courseId}
              test={currentTest}
              locked={!player.access.allowed}
            />
          ) : (
            <div className={`relative w-full overflow-hidden rounded-lg ${paneClass}`}>
              {source && current ? (
                <LessonPlayer
                  source={source}
                  title={current.title}
                  lessonId={current.id}
                  chrome={source.kind === 'video' ? 'native' : 'embed-strip'}
                  resumeAt={resumeAt}
                  completedAt={progressByLesson.get(current.id)?.completedAt ?? null}
                  guest={!player.access.allowed}
                  seekRestricted={seekRestricted}
                  watchedMax={progressByLesson.get(current.id)?.maxPositionSeconds ?? 0}
                  fullyWatchedAt={progressByLesson.get(current.id)?.fullyWatchedAt ?? null}
                  onEnded={onPlayerEnded}
                  onNeedsRefresh={refreshSource}
                  onAdapter={handleAdapter}
                  onAdapterEvent={heartbeat.onAdapterEvent}
                  onBookmark={() =>
                    bookmark.mutate({
                      lessonId: current.id,
                      positionSeconds: adapter
                        ? Math.floor(adapter.getSnapshot().currentTime)
                        : null,
                    })
                  }
                  overlay={upNextOverlay}
                />
              ) : (
                <div className="flex h-full items-center justify-center px-6 text-center text-sm text-white/70">
                  {sourceError ??
                    (current && !canPlay(current)
                      ? 'This lesson is locked.'
                      : current
                        ? 'Loading video…'
                        : 'Pick a lesson to start.')}
                </div>
              )}
            </div>
          )}
          {current && (
            <Card>
              <CardContent className="space-y-3 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="text-xs text-muted-foreground">{current.chapter.title}</div>
                    <h1 className="text-lg font-semibold">{current.title}</h1>
                  </div>
                  {canPlay(current) && (
                    <Button
                      size="sm"
                      variant={currentDone ? 'outline' : 'default'}
                      disabled={complete.isPending || uncomplete.isPending}
                      onClick={() =>
                        currentDone
                          ? uncomplete.mutate(current.id)
                          : complete.mutate({ lessonId: current.id, via: 'manual' })
                      }
                    >
                      {currentDone ? (
                        <>
                          <Check className="mr-1 h-4 w-4" /> Completed
                        </>
                      ) : (
                        'Mark complete'
                      )}
                    </Button>
                  )}
                </div>
                {current.description && (
                  <p className="whitespace-pre-line text-sm text-muted-foreground">
                    {current.description}
                  </p>
                )}
                <LessonResources
                  courseId={courseId}
                  lesson={current}
                  allowed={player.access.allowed}
                />
                <div className="flex justify-between">
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!prevLesson}
                    onClick={() => prevLesson && goTo(prevLesson.id)}
                  >
                    ← Previous
                  </Button>
                  {/* Verdict-driven: lands on a chapter quiz instead of stepping
                      over it, and says WHEN a dripped lesson opens. Still
                      clickable when locked — `/open` is what teaches. */}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={nextVerdict.kind === 'end'}
                    onClick={() => {
                      if (nextVerdict.kind === 'lesson') goTo(nextVerdict.lesson.id);
                      else if (nextVerdict.kind === 'quiz') goToTest(nextVerdict.test.id);
                      else if (nextVerdict.kind === 'locked') goTo(nextVerdict.lesson.id);
                    }}
                  >
                    {nextVerdict.kind === 'quiz'
                      ? `${nextVerdict.test.isPractice ? 'Practice' : 'Quiz'}: ${nextVerdict.test.title} →`
                      : nextVerdict.kind === 'locked'
                        ? `Unlocks ${unlockDate(nextVerdict.availableAt)}`
                        : nextVerdict.kind === 'end'
                          ? 'End of course'
                          : 'Next →'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
          {current && canPlay(current) && (
            <Card>
              <CardContent className="py-4">
                {/* Both panels stay mounted: a tab switch must not destroy a
                    half-typed question or note, and must not refire its query. */}
                <Tabs defaultValue={params.get('announcement') ? 'announcements' : 'qa'}>
                  <TabsList className="flex h-auto flex-wrap">
                    <TabsTrigger value="qa">Q&amp;A</TabsTrigger>
                    <TabsTrigger value="notes">Notes</TabsTrigger>
                    <TabsTrigger value="announcements">Announcements</TabsTrigger>
                  </TabsList>
                  {/* NOT forceMount, unlike Q&A and Notes: those hold typed
                      input a tab switch must not destroy. This one is read-only,
                      so mounting it lazily keeps the extra query off the page
                      for the learners who never open the tab. */}
                  <TabsContent value="announcements">
                    <CourseAnnouncements
                      courseId={courseId}
                      highlightId={params.get('announcement')}
                    />
                  </TabsContent>
                  <TabsContent value="qa" forceMount className="data-[state=inactive]:hidden">
                    <LessonQa
                      courseId={courseId}
                      lessonId={current.id}
                      highlightId={params.get('question')}
                    />
                  </TabsContent>
                  <TabsContent value="notes" forceMount className="data-[state=inactive]:hidden">
                    {player.access.allowed ? (
                      <LessonNotes
                        courseId={courseId}
                        lessonId={current.id}
                        lessonTitles={lessonTitles}
                        getCurrentTime={() => adapter?.getSnapshot().currentTime ?? null}
                        onSeek={seekTo}
                      />
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        Notes are available once you have access to this course.
                      </p>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          )}
        </div>

        <aside className="lg:sticky lg:top-20 lg:self-start">
          <Card>
            <CardContent className="p-0">
              <div className="border-b px-4 py-3 text-sm font-semibold">Course content</div>
              <div className="max-h-[70vh] overflow-auto">
                {player.chapters.map((ch) => (
                  <div key={ch.id}>
                    <div className="bg-muted/40 px-4 py-2 text-xs font-semibold text-muted-foreground">
                      {ch.title}
                    </div>
                    <ul>
                      {ch.lessons.map((l) => {
                        const p = progressByLesson.get(l.id);
                        const isCurrent = l.id === current?.id;
                        const playable = canPlay(l);
                        // Drip is decoration here: the row stays clickable so
                        // `/open` can answer with the real date (and, on a free
                        // course, create the enrollment the clock counts from).
                        const dripped = !!l.locked;
                        return (
                          <li key={l.id}>
                            <button
                              disabled={!playable}
                              onClick={() => goTo(l.id)}
                              className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm ${
                                isCurrent ? 'bg-accent font-medium' : 'hover:bg-muted/40'
                              } ${playable ? '' : 'opacity-50'}`}
                            >
                              {p?.completedAt ? (
                                <Check className="h-4 w-4 flex-none text-primary" />
                              ) : dripped ? (
                                <Lock className="h-4 w-4 flex-none text-muted-foreground" />
                              ) : playable ? (
                                <Circle className="h-4 w-4 flex-none text-muted-foreground" />
                              ) : (
                                <Lock className="h-4 w-4 flex-none text-muted-foreground" />
                              )}
                              <span className="flex-1 truncate">{l.title}</span>
                              {dripped && (
                                <span className="flex-none text-[11px] text-muted-foreground">
                                  Unlocks {unlockDate(l.availableAt)}
                                </span>
                              )}
                              {l.isPreview && !player.access.allowed && (
                                <Play className="h-3 w-3 flex-none text-muted-foreground" />
                              )}
                            </button>
                          </li>
                        );
                      })}
                      {player.tests
                        .filter((t) => t.chapterId === ch.id)
                        .map((t) => (
                          <TestRow
                            key={t.id}
                            test={t}
                            label={`${t.isPractice ? 'Practice' : 'Quiz'}: ${t.title}`}
                            isCurrent={t.id === currentTest?.id}
                            locked={!player.access.allowed}
                            onClick={() => goToTest(t.id)}
                          />
                        ))}
                    </ul>
                  </div>
                ))}
                {player.tests.some((t) => t.chapterId === null) && (
                  <div>
                    <div className="bg-muted/40 px-4 py-2 text-xs font-semibold text-muted-foreground">
                      Final test
                    </div>
                    <ul>
                      {player.tests
                        .filter((t) => t.chapterId === null)
                        .map((t) => (
                          <TestRow
                            key={t.id}
                            test={t}
                            label={t.isPractice ? `Practice: ${t.title}` : t.title}
                            isCurrent={t.id === currentTest?.id}
                            locked={!player.access.allowed}
                            onClick={() => goToTest(t.id)}
                          />
                        ))}
                    </ul>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </aside>
      </div>
    </div>
  );
}

/**
 * A lesson's downloadable handouts.
 *
 * The rows are shown to anyone who can see the lesson — a worksheet list tells
 * a browsing learner what the course actually includes — but the bytes stay
 * behind the download route, which requires full course access and an open
 * drip window. Hence the two states: a live Download for someone with access,
 * and a disabled row with an explanation for someone without.
 *
 * The button stays clickable when the lesson is drip-LOCKED. The lock on the
 * player payload is decoration (`services/course-availability.ts`); the route
 * is the authority, and the 403 it answers with carries the unlock date, which
 * is a better thing to show than a greyed-out control with no reason.
 */
function LessonResources({
  courseId,
  lesson,
  allowed,
}: {
  courseId: string;
  lesson: LessonOutline;
  allowed: boolean;
}) {
  const files = lesson.files ?? [];
  const download = useMutation({
    mutationFn: (fileId: string) => downloadLessonFile(courseId, lesson.id, fileId),
    onError: (e: unknown) => {
      const data = (e as { response?: { data?: { error?: string; availableAt?: string } } })
        ?.response?.data;
      toast.error(
        data?.error === 'lesson_not_available'
          ? `These resources unlock on ${unlockDate(data.availableAt)}.`
          : data?.error === 'purchase_required' || data?.error === 'subscription_required'
            ? 'Get the full course to download its resources.'
            : 'Could not start the download — try again.',
      );
    },
  });
  if (files.length === 0) return null;
  return (
    <div className="space-y-1 rounded border p-3">
      <p className="text-xs font-medium">Resources</p>
      <ul className="space-y-1">
        {files.map((f) => (
          <li key={f.id} className="flex items-center gap-2 text-sm">
            {allowed ? (
              <Download className="h-4 w-4 flex-none text-muted-foreground" />
            ) : (
              <Lock className="h-4 w-4 flex-none text-muted-foreground" />
            )}
            <span className="flex-1 truncate">{f.title}</span>
            <span className="flex-none text-xs tabular-nums text-muted-foreground">
              {(f.sizeBytes / 1048576).toFixed(1)} MB
            </span>
            <Button
              size="sm"
              variant="ghost"
              disabled={!allowed || download.isPending}
              onClick={() => download.mutate(f.id)}
            >
              Download
            </Button>
          </li>
        ))}
      </ul>
      {!allowed && <p className="text-xs text-muted-foreground">Included with the full course.</p>}
    </div>
  );
}

function TestRow({
  test,
  label,
  isCurrent,
  locked,
  onClick,
}: {
  test: { passed: boolean; isPractice: boolean; attempts: { status: string }[] };
  label: string;
  isCurrent: boolean;
  locked: boolean;
  onClick: () => void;
}) {
  const pending = test.attempts.some((a) => a.status === 'pending_review');
  // A practice test is never "failed" — it is a place to be wrong on purpose,
  // and the award glyph would read as certificate progress it does not carry.
  const failed = !test.isPractice && !test.passed && !pending && test.attempts.length > 0;
  return (
    <li>
      <button
        disabled={locked}
        onClick={onClick}
        className={`flex w-full items-center gap-2 px-4 py-2 text-left text-sm ${
          isCurrent ? 'bg-accent font-medium' : 'hover:bg-muted/40'
        } ${locked ? 'opacity-50' : ''}`}
      >
        {test.passed && test.isPractice ? (
          <Check className="h-4 w-4 flex-none text-primary" />
        ) : test.passed ? (
          <Award className="h-4 w-4 flex-none text-primary" />
        ) : pending ? (
          <Clock className="h-4 w-4 flex-none text-muted-foreground" />
        ) : failed ? (
          <XCircle className="h-4 w-4 flex-none text-destructive" />
        ) : locked ? (
          <Lock className="h-4 w-4 flex-none text-muted-foreground" />
        ) : (
          <ClipboardList className="h-4 w-4 flex-none text-muted-foreground" />
        )}
        <span className="flex-1 truncate">{label}</span>
      </button>
    </li>
  );
}
